import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Activity, BarChart3, Building2, Film, GraduationCap, Grid2x2, LayoutDashboard, ListVideo, LogOut, Menu, MonitorSmartphone, Plus, Siren, Smartphone, Sparkles, Users, X, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/api";
import { getSeen, SEEN_EVENT } from "@/lib/changelog";
import { ROLE_LABELS } from "@/lib/format";
import type { ChangelogEntry, Role } from "@/lib/types";
import { PairingDialog } from "@/components/app/PairingDialog";
import { LegalNotice } from "@/components/app/LegalNotice";

const NAV: { to: string; label: string; icon: any; roles: Role[]; id: string }[] = [
  { to: "/admin/organisations", label: "Organisations", icon: Building2, roles: ["super_admin"], id: "organizations" },
  { to: "/admin/apk", label: "Fichiers APK", icon: Smartphone, roles: ["super_admin"], id: "apk" },
  { to: "/tableau-de-bord", label: "Tableau de bord", icon: LayoutDashboard, roles: ["organization_admin"], id: "dashboard" },
  { to: "/ecrans", label: "Écrans", icon: MonitorSmartphone, roles: ["organization_admin"], id: "screens" },
  { to: "/groupes", label: "Groupes d'écrans", icon: Layers, roles: ["organization_admin"], id: "groups" },
  { to: "/murs-video", label: "Murs vidéo", icon: Grid2x2, roles: ["organization_admin"], id: "video-walls" },
  { to: "/alertes", label: "Alerte d'urgence", icon: Siren, roles: ["organization_admin"], id: "alerts" },
  { to: "/medias", label: "Médiathèque", icon: Film, roles: ["organization_admin", "editor"], id: "media" },
  { to: "/listes", label: "Listes de lecture", icon: ListVideo, roles: ["organization_admin", "editor"], id: "playlists" },
  { to: "/historique", label: "Historique", icon: Activity, roles: ["organization_admin"], id: "activity" },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3, roles: ["organization_admin"], id: "stats" },
  { to: "/utilisateurs", label: "Utilisateurs", icon: Users, roles: ["organization_admin"], id: "users" },
  { to: "/formation", label: "Formation", icon: GraduationCap, roles: ["super_admin", "organization_admin", "editor"], id: "training" },
  { to: "/mises-a-jour", label: "Mises à jour", icon: Sparkles, roles: ["super_admin", "organization_admin", "editor"], id: "updates" },
];

function useUnreadChangelog(userId: string | undefined) {
  const { data } = useApi<ChangelogEntry[]>(userId ? "/changelog" : null);
  const [seen, setSeen] = useState("");
  useEffect(() => {
    if (!userId) return;
    const sync = () => setSeen(getSeen(userId));
    sync();
    window.addEventListener(SEEN_EVENT, sync);
    return () => window.removeEventListener(SEEN_EVENT, sync);
  }, [userId]);
  return !!data?.length && data[0].created_at > seen;
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user, logout } = useAuth();
  const unread = useUnreadChangelog(user ? user.id : undefined);
  if (!user) return null;
  return (
    <div className="flex h-full flex-col">
      <div className="px-6 py-6">
        <img src="/img/logo-innova-sign.png" alt="Innova-Sign" data-testid="sidebar-logo" className="h-12 w-auto" />
        <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-orange-400">Cloud · Portail</p>
      </div>
      <div className="mx-4 mb-6 rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2.5">
        <p className="eyebrow">{user.role === "super_admin" ? "Plateforme" : "Organisation"}</p>
        <p data-testid="sidebar-org-name" className="mt-1 truncate text-sm font-medium text-slate-200">{user.organization_name || "Innova-Sign (global)"}</p>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.filter((n) => n.roles.includes(user.role)).map((n) => (
          <NavLink key={n.to} to={n.to} onClick={onNavigate} data-testid={`sidebar-nav-${n.id}`}
            className={({ isActive }) => `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-blue-600/15 text-blue-300 ring-1 ring-inset ring-blue-500/30" : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"}`}>
            <n.icon className="h-4 w-4" />{n.label}
            {n.id === "updates" && unread && <span data-testid="updates-unread-dot" className="ml-auto h-2 w-2 animate-pulse rounded-full bg-orange-400" />}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-800 p-4">
        <p data-testid="sidebar-user-name" className="truncate text-sm font-medium text-slate-200">{user.name}</p>
        <p className="truncate text-xs text-slate-500">{user.email}</p>
        <div className="mt-3 flex items-center justify-between">
          <span data-testid="sidebar-user-role" className="rounded border border-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">{ROLE_LABELS[user.role]}</span>
          <Button data-testid="btn-logout" variant="ghost" size="sm" onClick={logout} className="text-slate-400 hover:text-rose-300"><LogOut className="mr-1 h-4 w-4" />Quitter</Button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pairOpen, setPairOpen] = useState(false);
  const navigate = useNavigate();
  const canPair = user && user.role === "organization_admin";

  return (
    <div className="min-h-screen">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[260px] border-r border-slate-800/80 bg-slate-950/95 md:block"><Sidebar /></aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[260px] border-r border-slate-800 bg-slate-950"><Sidebar onNavigate={() => setMobileOpen(false)} /></aside>
        </div>
      )}
      <div className="md:pl-[260px]">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-800/80 bg-slate-950/85 px-4 backdrop-blur-xl md:px-8">
          <Button data-testid="btn-mobile-menu" variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>{mobileOpen ? <X /> : <Menu />}</Button>
          <p className="hidden font-mono text-xs text-slate-500 md:block">portail.innova-sign · FR-CA</p>
          {canPair && (
            <div className="flex gap-2">
              <Button data-testid="btn-header-alert" onClick={() => navigate("/alertes")} className="bg-red-600 text-white shadow-lg shadow-red-500/20 hover:bg-red-500"><Siren className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Alerte</span></Button>
              <Button data-testid="btn-pair-screen" onClick={() => setPairOpen(true)} className="bg-blue-600 text-white shadow-lg shadow-blue-500/20 hover:bg-blue-500"><Plus className="mr-1 h-4 w-4" />Jumeler un écran</Button>
            </div>
          )}
        </header>
        <main className="mx-auto max-w-[1400px] px-4 py-8 md:px-8 md:py-10"><Outlet context={{ openPairing: () => setPairOpen(true) }} /></main>
        <footer className="mx-auto max-w-[1400px] border-t border-slate-800/80 px-4 py-6 md:px-8"><LegalNotice /></footer>
      </div>
      {canPair && <PairingDialog open={pairOpen} onOpenChange={setPairOpen} />}
    </div>
  );
}
